/**
 * Products Bundle Component
 * Handles interactive dots, product highlighting, and bundle cart functionality
 */
class ProductsBundle {
  constructor(element) {
    this.element = element;
    this.dots = element.querySelectorAll('.products-bundle__dot');
    this.productItems = element.querySelectorAll('.products-bundle__product-item');
    this.addBundleButton = element.querySelector('.products-bundle__add-bundle');

    this.init();
  }

  init() {
    this.bindDotEvents();
    this.bindBundleButtonEvent();
    this.setupProductItemHover();
    this.setUpProductItemMouseLeave();
  }

  bindDotEvents() {
    this.dots.forEach((dot, index) => {
      dot.addEventListener('click', () => {
        this.highlightProduct(index + 1);
      });

      dot.addEventListener('keydown', (e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          this.highlightProduct(index + 1);
        }
      });
    });
  }

  bindBundleButtonEvent() {
    if (!this.addBundleButton) return;

    this.addBundleButton.addEventListener('click', (e) => {
      e.preventDefault();
      this.addBundleToCart();
    });
  }

  setupProductItemHover() {
    this.productItems.forEach((item, index) => {
      item.addEventListener('mouseenter', () => {
        this.highlightProduct(index + 1);
      });
    });
  }

  setUpProductItemMouseLeave() {
    this.productItems.forEach((item, index) => {
      item.addEventListener('mouseleave', () => {
        this.dots.forEach((dot) => dot.classList.remove('active'));
        this.productItems.forEach((item) => item.classList.remove('highlighted'));
      });
    });
  }

  highlightProduct(dotIndex) {
    // Add active states
    const targetDot = this.element.querySelector(`[data-dot-index="${dotIndex}"]`);
    const targetProduct = this.element.querySelector(`[data-item-index="${dotIndex}"]`);

    if (targetDot) {
      targetDot.classList.add('active');
    }

    if (targetProduct && targetProduct.classList.contains('products-bundle__product-item')) {
      targetProduct.classList.add('highlighted');
    }

    // Scroll product into view on mobile
    if (window.innerWidth < 750 && targetProduct) {
      targetProduct.scrollIntoView({
        behavior: 'smooth',
        block: 'nearest',
        inline: 'start',
      });
    }
  }

  async addBundleToCart() {
    if (!this.addBundleButton || this.addBundleButton.classList.contains('loading')) {
      return;
    }

    const bundleProducts = this.addBundleButton.dataset.bundleProducts;
    const productBundleTitle = this.addBundleButton.dataset.bundleTitle;
    const productBundleImage = this.addBundleButton.dataset.bundleImage;

    if (!bundleProducts) {
      console.error('No bundle products found');
      return;
    }

    const productIds = bundleProducts.split(',').map((id) => id.trim());

    try {
      this.setLoadingState(true);

      // 1. Add products normally (without requesting sections)
      const data = await this.addProductsToCart(productIds);

      // 2. Mark that the last add was a bundle using cart attributes
      await this.markAsBundle(productIds, productBundleTitle, productBundleImage);

      // 3. Get the updated sections after marking as bundle
      const updatedSections = await this.getBundleSections();

      this.handleAddToCartSuccess({ ...data, sections: updatedSections });
    } catch (error) {
      console.error('Error adding bundle to cart:', error);
      this.handleAddToCartError(error);
    } finally {
      this.setLoadingState(false);
    }
  }

  async addProductsToCart(productIds) {
    const items = productIds.map((id) => ({
      id: parseInt(id),
      quantity: 1,
    }));

    const config = {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
        'X-Requested-With': 'XMLHttpRequest',
      },
      body: JSON.stringify({
        items,
      }),
    };

    const response = await fetch('/cart/add.js', config);

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.message || 'Failed to add products to cart');
    }

    return await response.json();
  }

  async markAsBundle(productIds, productBundleTitle, productBundleImage) {
    const bundleInfo = {
      last_action: 'bundle_added',
      last_bundle_products: productIds.join(','),
      last_bundle_count: productIds.length,
      last_bundle_timestamp: Date.now(),
      last_bundle_title: productBundleTitle,
      last_bundle_image: productBundleImage,
    };

    await this.updateCartAttributes(bundleInfo);
  }

  async clearBundleMarker() {
    const clearInfo = {
      last_action: '',
      last_bundle_products: '',
      last_bundle_count: '',
      last_bundle_timestamp: '',
      last_bundle_title: '',
      last_bundle_image: '',
    };

    await this.updateCartAttributes(clearInfo);
  }

  async getBundleSections() {
    const cartNotification = document.querySelector('cart-notification');
    const cartType = cartNotification ? 'cartNotification' : 'cartDrawer';
    const sectionsPerCartType = {
      cartNotification: ['cart-notification-product', 'cart-notification-button', 'cart-icon-bubble'],
      cartDrawer: ['cart-icon-bubble', 'cart-drawer'],
    };

    const sectionsToRender = sectionsPerCartType[cartType];

    const response = await fetch(`/?sections=${sectionsToRender.join(',')}`);
    if (!response.ok) {
      throw new Error('Failed to get updated sections');
    }

    return await response.json();
  }

  async updateCartAttributes(attributes) {
    const config = {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
      },
      body: JSON.stringify({ attributes }),
    };

    const response = await fetch('/cart/update.js', config);
    return await response.json();
  }

  setLoadingState(loading) {
    if (loading) {
      this.addBundleButton.querySelector('.products-bundle__loading-spinner').classList.remove('hidden');
      this.addBundleButton.classList.add('loading');
      this.addBundleButton.setAttribute('aria-disabled', 'true');
    } else {
      this.addBundleButton.classList.remove('loading');
      this.addBundleButton.querySelector('.products-bundle__loading-spinner').classList.add('hidden');
      this.addBundleButton.setAttribute('aria-disabled', 'false');
    }
  }

  async handleAddToCartSuccess(data) {
    // Show success message
    const successMessage = this.addBundleButton.dataset.successMessage;
    this.showMessage(successMessage, 'success');

    const cartNotification = document.querySelector('cart-notification');
    const cartDrawer = document.querySelector('cart-drawer');
    const cartElement = cartNotification || cartDrawer;
    const cartType = cartNotification ? 'cartNotification' : 'cartDrawer';

    if (cartElement && cartElement.classList.contains('is-empty')) {
      cartElement.classList.remove('is-empty');
    }
    if (!cartElement) {
      console.warn('No cart element found');
      return;
    }

    if (typeof cartElement.renderContents === 'function') {
      cartElement.renderContents({
        sections: data.sections,
        key: cartType === 'cartNotification' ? 'bundle' : null,
      });
    }

    // Clear the bundle marker after showing notification
    setTimeout(() => {
      this.clearBundleMarker();
    }, 1000); // Wait 1 second to ensure notification is shown

    // Dispatch custom event for other components to listen to
    this.element.dispatchEvent(
      new CustomEvent('bundle:added', {
        bubbles: true,
        detail: {
          bundleProducts: this.addBundleButton.dataset.bundleProducts,
        },
      }),
    );
  }

  handleAddToCartError(error) {
    const errorMessage = this.addBundleButton.dataset.errorMessage;
    this.showMessage(error.message || errorMessage, 'error');
  }

  showMessage(message, type = 'info') {
    // Create or update message element
    let messageElement = this.element.querySelector('.products-bundle__message');

    if (!messageElement) {
      messageElement = document.createElement('div');
      messageElement.className = 'products-bundle__message';
      this.addBundleButton.parentNode.insertBefore(messageElement, this.addBundleButton.nextSibling);
    }

    messageElement.textContent = message;
    messageElement.className = `products-bundle__message alert__message alert__message--${type}`;
    messageElement.style.display = 'block';

    // Auto-hide message after 5 seconds
    setTimeout(() => {
      if (messageElement.parentNode) {
        messageElement.style.display = 'none';
      }
    }, 5000);
  }
}

// Initialize when DOM is ready
document.addEventListener('DOMContentLoaded', () => {
  const bundleSections = document.querySelectorAll('.products-bundle');

  bundleSections.forEach((section) => {
    if (!section.hasAttribute('data-bundle-initialized')) {
      const bundleInstance = new ProductsBundle(section);
      section.bundleInstance = bundleInstance; // Store reference for utility functions
      section.setAttribute('data-bundle-initialized', 'true');
    }
  });
});

// Additional utility functions for theme integration
window.ProductsBundleUtils = {
  // Function to highlight specific product
  highlightProduct: function (productIndex) {
    const bundleSection = document.querySelector('.products-bundle');
    if (bundleSection && bundleSection.bundleInstance) {
      bundleSection.bundleInstance.highlightProduct(productIndex);
    }
  },
};
